import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Play, Pause, X, FileDown, ChevronUp, ChevronDown, SkipBack, SkipForward,
  Type, ShieldAlert, Gauge, RotateCcw, FlipHorizontal, Camera, Video, Square, Mic, MicOff,
  Share2, RadioTower,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { formatDuration, retrancaSeconds } from "@/lib/timing";
import { renderPrompterText } from "@/lib/prompterMarkup";
import { BRAND_MONOGRAM } from "@/lib/brand";
import useCamera from "@/hooks/useCamera";
import useScreenWakeLock from "@/hooks/useScreenWakeLock";
import useMediaRecorder, { extensionFromMime } from "@/hooks/useMediaRecorder";
import useTeleprompterSync from "@/hooks/useTeleprompterSync";
import CameraPermissionModal from "@/components/CameraPermissionModal";
import CameraFloating from "@/components/CameraFloating";
import TPShareHub from "@/components/TPShareHub";

const SPEED_MIN = 20;
const SPEED_MAX = 600;
const SPEED_STEP = 10;
const FONT_MIN = 24;
const FONT_MAX = 160;
const FONT_STEP = 4;

const STORAGE_KEY = "podiumscript.tp.prefs";

const loadPrefs = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
};
const savePrefs = (p) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(p)); } catch {}
};

export default function Teleprompter({
  episode, onClose, onExportPdf, onUploadRecording,
  syncToken = null, allowShare = true,
}) {
  const prefs = useMemo(() => loadPrefs() || {}, []);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(prefs.speed ?? 120);
  const [fontSize, setFontSize] = useState(prefs.fontSize ?? 72);
  const [mirror, setMirror] = useState(!!prefs.mirror);
  const [vFlip, setVFlip] = useState(!!prefs.vFlip);
  const [controlsOpen, setControlsOpen] = useState(true);
  const userToggledRef = useRef(false);

  // --- Live sync (optional) ---
  // When a syncToken is active, play/pause/speed/fontSize are mirrored across
  // all peers (host + guests) via WebSocket.
  const [activeSyncToken, setActiveSyncToken] = useState(syncToken || null);
  const [shareOpen, setShareOpen] = useState(false);
  const applyingRemoteRef = useRef(false);
  useEffect(() => { setActiveSyncToken(syncToken || null); }, [syncToken]);

  const applyRemote = useCallback((data) => {
    applyingRemoteRef.current = true;
    if (typeof data.playing === "boolean") setPlaying(data.playing);
    if (typeof data.speed === "number") setSpeed(data.speed);
    if (typeof data.fontSize === "number") setFontSize(data.fontSize);
    if (data.resetTop && containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
    // Clear flag after React commits
    setTimeout(() => { applyingRemoteRef.current = false; }, 0);
  }, []);

  const sync = useTeleprompterSync({
    token: activeSyncToken,
    enabled: !!activeSyncToken,
    onEvent: (data) => {
      if (!data || typeof data !== "object") return;
      if (data.type === "state" || data.type === "sync") applyRemote(data);
      else if (data.type === "reset") applyRemote({ resetTop: true, playing: false });
    },
  });

  // Broadcast local state changes to peers. Skipped when a remote update is
  // currently being applied, to avoid feedback loops.
  useEffect(() => {
    if (!activeSyncToken || !sync.connected) return;
    if (applyingRemoteRef.current) return;
    sync.send({ type: "state", playing, speed, fontSize });
  }, [playing, speed, fontSize, activeSyncToken, sync.connected, sync]);

  // --- Camera & recording state ---
  const camera = useCamera();
  const recorder = useMediaRecorder();
  const [showCamModal, setShowCamModal] = useState(false);
  const [audioOn, setAudioOn] = useState(prefs.audioOn ?? true);
  const [uploading, setUploading] = useState(false);
  const combinedStreamRef = useRef(null);
  useScreenWakeLock(true);

  // Persist prefs including camera options
  useEffect(() => {
    savePrefs({ speed, fontSize, mirror, vFlip, audioOn });
  }, [speed, fontSize, mirror, vFlip, audioOn]);

  // Auto-hide controls when a PHONE rotates to landscape (prompter in use mode).
  // Scoped to touch devices on small viewports so desktop landscape monitors
  // don't trigger the auto-hide.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(
      "(orientation: landscape) and (max-width: 1023px) and (pointer: coarse)"
    );
    const applyFromOrientation = () => {
      if (userToggledRef.current) return;
      setControlsOpen(!mq.matches);
    };
    applyFromOrientation();
    const handler = () => applyFromOrientation();
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler); // Safari fallback
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, []);

  const toggleControls = useCallback(() => {
    userToggledRef.current = true;
    setControlsOpen((o) => !o);
  }, []);

  const containerRef = useRef(null);
  const rafRef = useRef(null);
  const lastTsRef = useRef(null);
  const scrollAccRef = useRef(0);
  const retrancaRefs = useRef({});
  const touchStartYRef = useRef(null);

  // Persist prefs
  useEffect(() => { /* consolidated above */ }, []);

  const fullScript = useMemo(() => (
    episode.retrancas
      .filter((r) => r.approved)
      .map((r) => ({
        id: r.id, title: r.title, type: r.type, presenter: r.presenter,
        text: r.text || "",
        uppercase: !!r.uppercase,
        vt_duration: r.vt_duration || 0,
      }))
  ), [episode]);

  const skippedCount = useMemo(
    () => episode.retrancas.filter((r) => !r.approved).length,
    [episode]
  );
  const totalDurationSec = useMemo(
    () => fullScript.reduce((acc, r) => acc + retrancaSeconds(r), 0),
    [fullScript]
  );

  // --- Body scroll lock (critical on iOS so overlay doesn't scroll the page behind) ---
  useEffect(() => {
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyPosition = document.body.style.position;
    const scrollY = window.scrollY;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    // iOS Safari needs position:fixed to actually lock scroll
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.position = prevBodyPosition;
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      window.scrollTo(0, scrollY);
    };
  }, []);

  // --- Auto-scroll via RAF (sub-pixel accumulator, dt clamp, no CSS smooth) ---
  useEffect(() => {
    if (!playing) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      lastTsRef.current = null;
      scrollAccRef.current = 0;
      return;
    }

    const tick = (ts) => {
      const el = containerRef.current;
      if (!el) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      if (lastTsRef.current === null) {
        lastTsRef.current = ts;
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      const dtRaw = (ts - lastTsRef.current) / 1000;
      const dt = Math.min(dtRaw, 0.1); // clamp when tab was backgrounded
      lastTsRef.current = ts;

      scrollAccRef.current += speed * dt;
      const whole = Math.floor(scrollAccRef.current);
      if (whole > 0) {
        el.scrollTop = el.scrollTop + whole;
        scrollAccRef.current -= whole;
      }

      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 2) {
        setPlaying(false);
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [playing, speed]);

  // Reset timing when speed changes mid-play (prevents visual stutter)
  useEffect(() => {
    scrollAccRef.current = 0;
    lastTsRef.current = null;
  }, [speed]);

  // --- Navigation ---
  const scrollToRetranca = useCallback((idx) => {
    const r = fullScript[idx];
    const container = containerRef.current;
    if (!r || !container) return;
    const el = retrancaRefs.current[r.id];
    if (!el) return;
    const line = container.clientHeight / 3;
    const target = el.offsetTop - line;
    container.scrollTo({ top: Math.max(0, target), behavior: "smooth" });
  }, [fullScript]);

  const currentIndex = useCallback(() => {
    const container = containerRef.current;
    if (!container) return 0;
    const line = container.scrollTop + container.clientHeight / 3;
    let idx = 0;
    for (let i = 0; i < fullScript.length; i++) {
      const el = retrancaRefs.current[fullScript[i].id];
      if (el && el.offsetTop <= line) idx = i;
    }
    return idx;
  }, [fullScript]);

  const jumpPrev = useCallback(() => {
    scrollToRetranca(Math.max(0, currentIndex() - 1));
  }, [currentIndex, scrollToRetranca]);

  const jumpNext = useCallback(() => {
    scrollToRetranca(Math.min(fullScript.length - 1, currentIndex() + 1));
  }, [currentIndex, scrollToRetranca, fullScript.length]);

  const reset = useCallback(() => {
    if (containerRef.current) containerRef.current.scrollTop = 0;
    scrollAccRef.current = 0;
    setPlaying(false);
    // Broadcast an explicit reset so peers scroll to top too
    if (activeSyncToken && sync.connected && !applyingRemoteRef.current) {
      sync.send({ type: "reset" });
    }
  }, [activeSyncToken, sync]);

  // --- Camera + Recording actions ---
  const requestCamera = useCallback(() => {
    if (!camera.supported) {
      toast.error("Este navegador não suporta câmera.");
      return;
    }
    if (camera.state === "blocked") {
      setShowCamModal(true);
      return;
    }
    // Show the explanatory modal before the native dialog
    setShowCamModal(true);
  }, [camera.supported, camera.state]);

  const confirmCameraStart = useCallback(async () => {
    setShowCamModal(false);
    await camera.start();
  }, [camera]);

  const stopCamera = useCallback(() => {
    if (recorder.state === "recording") recorder.stop();
    camera.stop();
  }, [camera, recorder]);

  // Build a combined (video + optional audio) stream for recording
  const buildRecordingStream = useCallback(async () => {
    const videoStream = camera.stream;
    if (!videoStream) return null;
    if (!audioOn) return videoStream;
    try {
      const audio = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      const combined = new MediaStream([
        ...videoStream.getVideoTracks(),
        ...audio.getAudioTracks(),
      ]);
      combinedStreamRef.current = combined;
      return combined;
    } catch {
      toast.message("Microfone indisponível — gravando sem áudio.");
      return videoStream;
    }
  }, [camera.stream, audioOn]);

  const startRecording = useCallback(async () => {
    const s = await buildRecordingStream();
    if (!s) {
      toast.error("Ative a câmera antes de gravar.");
      return;
    }
    await recorder.start(s);
  }, [buildRecordingStream, recorder]);

  const stopRecordingAndStop = useCallback(() => {
    recorder.stop();
    const combined = combinedStreamRef.current;
    if (combined) {
      combined.getAudioTracks().forEach((t) => { try { t.stop(); } catch {} });
      combinedStreamRef.current = null;
    }
  }, [recorder]);

  // Build filename when blob is ready, trigger download, offer upload
  useEffect(() => {
    if (!recorder.blob) return;
    const ext = extensionFromMime(recorder.mime);
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}h${pad(now.getMinutes())}`;
    const safeTitle = (episode?.title || "LAUDA")
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 40);
    const filename = `${safeTitle}_${stamp}.${ext}`;

    // Trigger download
    const url = URL.createObjectURL(recorder.blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    toast.success(`Gravação salva: ${filename}`);

    // Optional upload
    if (onUploadRecording) {
      setUploading(true);
      onUploadRecording(recorder.blob, filename, recorder.mime)
        .then(() => toast.success("Vídeo enviado para a lauda."))
        .catch((e) => toast.error(e?.message || "Falha ao enviar o vídeo."))
        .finally(() => {
          setUploading(false);
          recorder.reset();
        });
    } else {
      recorder.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recorder.blob]);

  // Full cleanup on unmount
  useEffect(() => () => {
    camera.stop();
    const combined = combinedStreamRef.current;
    if (combined) combined.getTracks().forEach((t) => { try { t.stop(); } catch {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Keyboard shortcuts ---
  useEffect(() => {
    const handler = (e) => {
      const tag = (e.target && e.target.tagName) || "";
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (e.code === "Space") { e.preventDefault(); setPlaying((p) => !p); }
      else if (e.code === "Escape") onClose();
      else if (e.code === "ArrowUp") { e.preventDefault(); setSpeed((s) => Math.min(SPEED_MAX, s + SPEED_STEP)); }
      else if (e.code === "ArrowDown") { e.preventDefault(); setSpeed((s) => Math.max(SPEED_MIN, s - SPEED_STEP)); }
      else if (e.code === "ArrowRight" || e.code === "PageDown") { e.preventDefault(); jumpNext(); }
      else if (e.code === "ArrowLeft" || e.code === "PageUp") { e.preventDefault(); jumpPrev(); }
      else if (e.key === "+" || e.key === "=") { e.preventDefault(); setFontSize((s) => Math.min(FONT_MAX, s + FONT_STEP)); }
      else if (e.key === "-" || e.key === "_") { e.preventDefault(); setFontSize((s) => Math.max(FONT_MIN, s - FONT_STEP)); }
      else if (e.key === "Home") { e.preventDefault(); reset(); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose, jumpNext, jumpPrev, reset]);

  // --- Touch tap to toggle play/pause (iPhone / tablet friendly) ---
  const onTouchStart = (e) => {
    if (e.touches && e.touches.length === 1) {
      touchStartYRef.current = { y: e.touches[0].clientY, t: Date.now() };
    } else {
      touchStartYRef.current = null;
    }
  };
  const onTouchEnd = (e) => {
    const start = touchStartYRef.current;
    touchStartYRef.current = null;
    if (!start) return;
    const changed = e.changedTouches && e.changedTouches[0];
    if (!changed) return;
    const dy = Math.abs(changed.clientY - start.y);
    const dt = Date.now() - start.t;
    // Treat as tap: short, little vertical movement
    if (dy < 10 && dt < 300) {
      setPlaying((p) => !p);
    }
  };

  // Transform is applied ONLY to the inner content wrapper, never to the
  // scroll container itself. Applying transform to the scroll container
  // breaks scrollTop/scrollHeight on iOS Safari.
  const contentTransform = useMemo(() => {
    const sx = mirror ? -1 : 1;
    const sy = vFlip ? -1 : 1;
    if (sx === 1 && sy === 1) return "none";
    return `scale(${sx}, ${sy})`;
  }, [mirror, vFlip]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black text-white select-none"
      data-testid="teleprompter-overlay"
      style={{ height: "100dvh", width: "100dvw" }}
    >
      <div
        ref={containerRef}
        className="absolute inset-0 overflow-y-auto overflow-x-hidden"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        style={{
          scrollBehavior: "auto",
          WebkitOverflowScrolling: "touch",
          overscrollBehavior: "contain",
          touchAction: "pan-y",
          willChange: "scroll-position",
        }}
      >
        <div
          className="max-w-5xl mx-auto px-6 sm:px-12"
          style={{
            paddingTop: "40vh",
            paddingBottom: "60vh",
            fontFamily: "Outfit, sans-serif",
            transform: contentTransform,
            transformOrigin: "center top",
          }}
        >
          {fullScript.length === 0 && (
            <div className="text-zinc-500 text-2xl sm:text-3xl">
              Nenhuma retranca aprovada. Aprove ao menos uma no espelho para exibir aqui.
            </div>
          )}
          {skippedCount > 0 && fullScript.length > 0 && (
            <div className="mb-12 inline-flex items-center gap-2 text-amber-400/80 text-sm sm:text-lg uppercase tracking-[0.2em]">
              <ShieldAlert className="w-4 h-4 sm:w-5 sm:h-5" />
              {skippedCount} retranca{skippedCount > 1 ? "s" : ""} não aprovada{skippedCount > 1 ? "s" : ""} — ocultas
            </div>
          )}
          {fullScript.map((r, i) => (
            <div
              key={r.id}
              ref={(el) => { if (el) retrancaRefs.current[r.id] = el; }}
              className="mb-20 sm:mb-24"
              data-testid={`tp-retranca-${r.id}`}
            >
              <div className="text-amber-400 text-sm sm:text-2xl mb-4 sm:mb-6 uppercase tracking-[0.2em] font-bold opacity-90">
                {String(i + 1).padStart(2, "0")} · {r.type} · {r.title}
                {r.presenter && (
                  <span className="text-zinc-400 normal-case tracking-normal"> — {r.presenter}</span>
                )}
                {r.type === "VT" && r.vt_duration > 0 && (
                  <span className="text-zinc-400 normal-case tracking-normal"> · {formatDuration(r.vt_duration)}</span>
                )}
              </div>
              <div
                className="font-bold whitespace-pre-wrap break-words"
                style={{
                  fontSize: `${fontSize}px`,
                  lineHeight: 1.3,
                  letterSpacing: "-0.01em",
                }}
              >
                {r.text
                  ? renderPrompterText(r.uppercase ? r.text.toUpperCase() : r.text)
                  : <span className="text-zinc-600 italic">(sem texto)</span>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Reading line */}
      <div className="pointer-events-none absolute left-0 right-0 top-1/3 border-t-2 border-amber-400/50" />

      {/* Floating camera preview + permission modal */}
      {camera.state === "granted" && camera.stream && (
        <CameraFloating
          stream={camera.stream}
          onClose={stopCamera}
          onFlip={camera.flip}
          recording={recorder.state === "recording"}
          elapsed={recorder.elapsed}
        />
      )}
      <CameraPermissionModal
        open={showCamModal}
        mode={camera.state === "blocked" ? "blocked" : "prompt"}
        onConfirm={confirmCameraStart}
        onCancel={() => setShowCamModal(false)}
      />

      {/* Floating recording indicator when controls are hidden */}
      {!controlsOpen && recorder.state === "recording" && (
        <div
          data-testid="tp-recording-badge"
          className="pointer-events-none absolute top-3 right-3 bg-red-600/90 backdrop-blur-sm text-white rounded-sm px-3 py-1.5 flex items-center gap-2 shadow-lg"
        >
          <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
          <span className="text-xs font-mono font-bold tracking-wider">
            REC {String(Math.floor(recorder.elapsed / 60)).padStart(2, "0")}:{String(recorder.elapsed % 60).padStart(2, "0")}
          </span>
        </div>
      )}

      {/* Side gutters */}
      <div className="pointer-events-none absolute inset-y-0 left-0 w-[6%] sm:w-[8%] bg-gradient-to-r from-black/40 to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-[6%] sm:w-[8%] bg-gradient-to-l from-black/40 to-transparent" />

      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 p-3 sm:p-4 flex items-center justify-between bg-gradient-to-b from-black/90 to-transparent pointer-events-none">
        <div className="flex items-center gap-2 sm:gap-3 pointer-events-auto min-w-0">
          <img src={BRAND_MONOGRAM} alt="PodiUmScript" className="h-6 sm:h-7 w-auto opacity-90 shrink-0" />
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.3em] font-bold text-zinc-300 truncate">
            Teleprompter · <span className="text-amber-300 font-mono">{formatDuration(totalDurationSec)}</span>
          </div>
          {activeSyncToken && (
            <div
              data-testid="tp-live-badge"
              className={`hidden sm:flex items-center gap-1.5 ml-2 px-2 py-1 rounded-sm border text-[10px] uppercase tracking-[0.2em] font-bold ${sync.connected ? "border-red-500 text-red-400 bg-red-500/10" : "border-zinc-600 text-zinc-500"}`}
              title={sync.connected ? "Controles sincronizados ao vivo" : "Conectando..."}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${sync.connected ? "bg-red-500 animate-pulse" : "bg-zinc-500"}`} />
              Ao vivo · {sync.peers} {sync.peers === 1 ? "aparelho" : "aparelhos"}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 sm:gap-2 pointer-events-auto shrink-0">
          {allowShare && (
            <Button
              variant="ghost" size="sm" data-testid="tp-open-share"
              onClick={() => setShareOpen(true)}
              className={`rounded-sm h-9 px-2 sm:px-3 ${activeSyncToken ? "text-amber-300 hover:text-amber-200 hover:bg-amber-400/10" : "text-zinc-300 hover:text-white hover:bg-white/10"}`}
              title="Compartilhar teleprompter por link"
            >
              {activeSyncToken ? <RadioTower className="w-4 h-4 sm:mr-2" /> : <Share2 className="w-4 h-4 sm:mr-2" />}
              <span className="hidden sm:inline">{activeSyncToken ? "Ao vivo" : "Compartilhar"}</span>
            </Button>
          )}
          <Button
            variant="ghost" size="sm" data-testid="tp-export-pdf"
            onClick={onExportPdf}
            className="text-zinc-300 hover:text-white hover:bg-white/10 rounded-sm h-9 px-2 sm:px-3"
          >
            <FileDown className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">PDF</span>
          </Button>
          <Button
            variant="ghost" size="sm" data-testid="tp-close"
            onClick={onClose}
            className="text-zinc-300 hover:text-white hover:bg-white/10 rounded-sm h-9 w-9 p-0"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Bottom controls (responsive, visible on mobile) */}
      <div className="absolute bottom-0 left-0 right-0 p-2 sm:p-4 pointer-events-none">
        <div className="max-w-4xl mx-auto bg-black/85 backdrop-blur-xl border border-white/10 rounded-sm pointer-events-auto overflow-hidden">
          {/* Collapsible toggle: visible on mobile portrait always; in landscape
              only when controls are hidden (so there is still a way to reopen).
              Safe on desktop because the scoped matchMedia never flips
              controlsOpen to false there — meaning the "block" branch below
              will only run on touch devices in practice. */}
          <button
            data-testid="tp-controls-toggle"
            onClick={toggleControls}
            className={`${controlsOpen ? "block sm:hidden" : "block"} w-full text-center text-[9px] uppercase tracking-[0.3em] text-zinc-400 py-1 border-b border-white/5`}
          >
            {controlsOpen ? "Esconder controles ▾" : "Mostrar controles ▴"}
          </button>

          <div
            className="p-3 sm:p-4"
            style={{ display: controlsOpen ? "block" : "none" }}
          >
            {/* Row 1: Transport */}
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              <Button
                size="icon"
                data-testid="tp-play-pause"
                onClick={() => setPlaying((p) => !p)}
                className="w-11 h-11 sm:w-12 sm:h-12 rounded-sm bg-[#152D4B] hover:bg-[#0E1F36] shrink-0"
                title="Play / Pause (Espaço · ou toque na tela)"
              >
                {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
              </Button>
              <button
                data-testid="tp-jump-prev"
                onClick={jumpPrev}
                className="w-10 h-10 rounded-sm border border-white/10 hover:bg-white/10 flex items-center justify-center text-zinc-200 shrink-0"
                title="Retranca anterior"
              >
                <SkipBack className="w-4 h-4" />
              </button>
              <button
                data-testid="tp-jump-next"
                onClick={jumpNext}
                className="w-10 h-10 rounded-sm border border-white/10 hover:bg-white/10 flex items-center justify-center text-zinc-200 shrink-0"
                title="Próxima retranca"
              >
                <SkipForward className="w-4 h-4" />
              </button>
              <button
                data-testid="tp-reset"
                onClick={reset}
                className="h-10 px-2 sm:px-3 rounded-sm border border-white/10 hover:bg-white/10 flex items-center gap-1.5 text-zinc-200 text-[10px] uppercase tracking-[0.15em] shrink-0"
                title="Reiniciar (Home)"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Reiniciar</span>
              </button>
              <div className="ml-auto flex items-center gap-1 sm:gap-2">
                {/* Camera on/off */}
                {camera.supported !== false && (
                  <button
                    data-testid="tp-camera-toggle"
                    onClick={camera.state === "granted" ? stopCamera : requestCamera}
                    className={`h-10 px-2 sm:px-3 rounded-sm border flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] ${camera.state === "granted" ? "border-amber-400 text-amber-400" : "border-white/10 text-zinc-300 hover:bg-white/10"}`}
                    title={camera.state === "granted" ? "Desligar câmera" : "Ligar câmera"}
                  >
                    <Camera className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">{camera.state === "granted" ? "Câmera ON" : "Câmera"}</span>
                  </button>
                )}
                {/* Record / Stop */}
                {recorder.supported && camera.state === "granted" && (
                  <>
                    <button
                      data-testid="tp-audio-toggle"
                      onClick={() => setAudioOn((a) => !a)}
                      disabled={recorder.state === "recording"}
                      className={`h-10 px-2 rounded-sm border flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] disabled:opacity-50 ${audioOn ? "border-emerald-400 text-emerald-400" : "border-white/10 text-zinc-400 hover:bg-white/10"}`}
                      title={audioOn ? "Áudio ligado" : "Áudio desligado"}
                    >
                      {audioOn ? <Mic className="w-3.5 h-3.5" /> : <MicOff className="w-3.5 h-3.5" />}
                    </button>
                    {recorder.state === "recording" ? (
                      <button
                        data-testid="tp-record-stop"
                        onClick={stopRecordingAndStop}
                        className="h-10 px-2 sm:px-3 rounded-sm bg-red-600 hover:bg-red-700 text-white flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em]"
                      >
                        <Square className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">
                          Parar {String(Math.floor(recorder.elapsed / 60)).padStart(2, "0")}:{String(recorder.elapsed % 60).padStart(2, "0")}
                        </span>
                        <span className="sm:hidden font-mono">
                          {String(Math.floor(recorder.elapsed / 60)).padStart(2, "0")}:{String(recorder.elapsed % 60).padStart(2, "0")}
                        </span>
                      </button>
                    ) : (
                      <button
                        data-testid="tp-record-start"
                        onClick={startRecording}
                        disabled={uploading}
                        className="h-10 px-2 sm:px-3 rounded-sm border border-red-500 text-red-400 hover:bg-red-500/10 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] disabled:opacity-50"
                        title="Iniciar gravação"
                      >
                        <Video className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">{uploading ? "Enviando..." : "Gravar"}</span>
                      </button>
                    )}
                  </>
                )}
                <button
                  data-testid="tp-mirror"
                  onClick={() => setMirror((m) => !m)}
                  className={`h-10 px-2 sm:px-3 rounded-sm border flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] ${mirror ? "border-amber-400 text-amber-400" : "border-white/10 text-zinc-300 hover:bg-white/10"}`}
                  title="Espelhar horizontalmente (prompter físico)"
                >
                  <FlipHorizontal className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Espelhar</span>
                </button>
                <button
                  data-testid="tp-vflip"
                  onClick={() => setVFlip((v) => !v)}
                  className={`h-10 px-2 sm:px-3 rounded-sm border flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] ${vFlip ? "border-amber-400 text-amber-400" : "border-white/10 text-zinc-300 hover:bg-white/10"}`}
                  title="Inverter verticalmente"
                >
                  <FlipHorizontal className="w-3.5 h-3.5 rotate-90" />
                  <span className="hidden sm:inline">Invert.</span>
                </button>
              </div>
            </div>

            {/* Row 2: Speed */}
            <div className="mt-3 sm:mt-4 flex items-center gap-2 sm:gap-3">
              <div className="hidden sm:flex items-center gap-1.5 text-zinc-300 min-w-[88px]">
                <Gauge className="w-4 h-4 text-zinc-400" />
                <span className="text-[10px] uppercase tracking-[0.2em]">Velocidade</span>
              </div>
              <Gauge className="w-4 h-4 text-zinc-400 sm:hidden shrink-0" />
              <button
                data-testid="tp-speed-down"
                onClick={() => setSpeed((s) => Math.max(SPEED_MIN, s - SPEED_STEP))}
                className="w-9 h-9 border border-white/10 rounded-sm hover:bg-white/10 flex items-center justify-center text-zinc-300 text-lg shrink-0"
              >−</button>
              <div className="flex-1 min-w-0">
                <Slider
                  value={[speed]}
                  min={SPEED_MIN} max={SPEED_MAX} step={SPEED_STEP}
                  data-testid="tp-speed-slider"
                  onValueChange={(v) => setSpeed(v[0])}
                />
              </div>
              <button
                data-testid="tp-speed-up"
                onClick={() => setSpeed((s) => Math.min(SPEED_MAX, s + SPEED_STEP))}
                className="w-9 h-9 border border-white/10 rounded-sm hover:bg-white/10 flex items-center justify-center text-zinc-300 text-lg shrink-0"
              >+</button>
              <span
                data-testid="tp-speed-value"
                className="text-xs font-mono text-amber-300 min-w-[52px] sm:min-w-[64px] text-right shrink-0"
              >
                {speed}
              </span>
            </div>

            {/* Row 3: Font size */}
            <div className="mt-3 flex items-center gap-2 sm:gap-3">
              <div className="hidden sm:flex items-center gap-1.5 text-zinc-300 min-w-[88px]">
                <Type className="w-4 h-4 text-zinc-400" />
                <span className="text-[10px] uppercase tracking-[0.2em]">Tamanho</span>
              </div>
              <Type className="w-4 h-4 text-zinc-400 sm:hidden shrink-0" />
              <button
                data-testid="tp-font-down"
                onClick={() => setFontSize((s) => Math.max(FONT_MIN, s - FONT_STEP))}
                className="w-9 h-9 border border-white/10 rounded-sm hover:bg-white/10 flex items-center justify-center text-zinc-300 shrink-0"
              >
                <ChevronDown className="w-4 h-4" />
              </button>
              <div className="flex-1 min-w-0">
                <Slider
                  value={[fontSize]}
                  min={FONT_MIN} max={FONT_MAX} step={FONT_STEP}
                  data-testid="tp-font-slider"
                  onValueChange={(v) => setFontSize(v[0])}
                />
              </div>
              <button
                data-testid="tp-font-up"
                onClick={() => setFontSize((s) => Math.min(FONT_MAX, s + FONT_STEP))}
                className="w-9 h-9 border border-white/10 rounded-sm hover:bg-white/10 flex items-center justify-center text-zinc-300 shrink-0"
              >
                <ChevronUp className="w-4 h-4" />
              </button>
              <span
                data-testid="tp-font-value"
                className="text-xs font-mono text-amber-300 min-w-[52px] sm:min-w-[64px] text-right shrink-0"
              >
                {fontSize}px
              </span>
            </div>
          </div>
        </div>
        <div className="hidden sm:block text-center mt-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500">
          Espaço: play/pause · ↑↓: velocidade · +/−: fonte · ←→: pular retranca · Toque na tela: play/pause · Esc: fechar
        </div>
      </div>

      {allowShare && (
        <TPShareHub
          open={shareOpen}
          onOpenChange={setShareOpen}
          episode={episode}
          activeSyncToken={activeSyncToken}
          onSelectSyncToken={setActiveSyncToken}
        />
      )}
    </div>
  );
}
